import { format } from 'date-fns';
import { Calendar as CalendarIcon, Clock, User, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

export default function QuickBookingPopover({
  slot,         // { start: Date, end: Date, resourceId?: string, bounds?: DOMRect }
  providers,    // provider list for resource name lookup
  onCreateDraft,
  onClose,
  isMobile = false,
}) {
  if (!slot) return null;

  const providerName = slot.resourceId
    ? providers.find(p => p.id === slot.resourceId)?.name
    : null;

  const handleCreate = () => {
    onCreateDraft({
      confirmedDate: format(slot.start, 'yyyy-MM-dd'),
      confirmedTime: format(slot.start, 'HH:mm'),
      providerId: slot.resourceId || '',
    });
  };

  const content = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-stone-900">New Booking</h4>
        <button
          onClick={onClose}
          className="p-1 rounded-md hover:bg-stone-100 text-stone-400 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2.5 text-sm text-stone-700">
          <CalendarIcon size={14} className="text-stone-400 shrink-0" />
          <span className="font-medium">{format(slot.start, 'EEEE, d MMMM yyyy')}</span>
        </div>
        <div className="flex items-center gap-2.5 text-sm text-stone-700">
          <Clock size={14} className="text-stone-400 shrink-0" />
          <span className="font-medium">
            {format(slot.start, 'HH:mm')} – {format(slot.end, 'HH:mm')}
          </span>
        </div>
        {providerName && (
          <div className="flex items-center gap-2.5 text-sm text-stone-700">
            <User size={14} className="text-stone-400 shrink-0" />
            <span className="font-medium">{providerName}</span>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          onClick={handleCreate}
          className="flex-1 bg-stone-900 text-white hover:bg-stone-800 h-9 text-xs font-semibold"
        >
          Create Booking
        </Button>
        <Button
          variant="outline"
          onClick={onClose}
          className="h-9 text-xs font-semibold px-4"
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  // Mobile: bottom sheet
  if (isMobile) {
    return (
      <>
        <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={onClose} />
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl border-t border-stone-200 shadow-2xl p-5 animate-in slide-in-from-bottom duration-200">
          <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-4" />
          {content}
        </div>
      </>
    );
  }

  // Desktop: popover
  return (
    <Popover open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <PopoverAnchor className="fixed" style={{ left: '50%', top: '40%' }} />
      <PopoverContent className="w-72 p-4" side="right" align="start">
        {content}
      </PopoverContent>
    </Popover>
  );
}
